import * as Font from "expo-font"
import React, { useEffect, useState } from "react"
import { Provider } from 'react-redux'
import store, { useAppDispatch } from "./src/store"
import { QueryClient, QueryClientProvider } from 'react-query'
import { loadOrCreateDeviceId } from "./src/auth-slice"
import { checkForUpdates, version } from './src/utility/updates'
import { RouterType, RouterContext } from './src/router-context'
import { extra } from './src/config'
import { StatusBar } from 'expo-status-bar'
import FullScreenLoading from "./src/utility/full-screen-loading"
import { PopoverProvider } from "./src/utility/popover"
import { Platform } from "react-native"
import {
  AntDesign,
  Entypo,
  EvilIcons,
  Feather,
  FontAwesome,
  Fontisto,
  Foundation,
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
  Octicons,
  SimpleLineIcons,
  Zocial
} from "@expo/vector-icons"
import { SafeAreaProvider } from "react-native-safe-area-context"
import { useAppClosure } from "./src/hooks"
import { log } from "./src/utility"
import NavigationContainerWrapper from "./src/navigation/navigation-container-wrapper"
import { PermissionsProvider, TrackingProvider, ThemeProvider, Auth0Provider, AuthProviderWrapper } from "./src/utility/context"
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet"
import { GestureHandlerRootView } from "react-native-gesture-handler"
import * as Sentry from "./src/utility/sentry"
import ErrorFallback from "./src/error-fallback"
import pseudoUuid from "./src/pseudo-uuid"
import Analytics from "./src/utility/analytics"
import CommunityImpactEmbedding from "./src/embeddings/community-impact"

const WEB_EMBED_PATH_PREFIX = "/web/community-impact"

// Resolved once at module load so a single render decides the branch and we
// never transiently mount Auth0/Plaid providers in the embed iframe.
const isWebEmbed: boolean =
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  typeof window.location?.pathname === "string" &&
  window.location.pathname.startsWith(WEB_EMBED_PATH_PREFIX)

type EmbedTheme = 'light' | 'dark' | 'climateFirst'

// Only these tokens may flow from the URL into ThemeProvider. Anything else
// (including missing or malformed input) falls back to 'light'. This breaks
// taint propagation from window.location.search at the boundary.
const ALLOWED_EMBED_THEMES: ReadonlyArray<EmbedTheme> = ['light', 'dark', 'climateFirst']

const readEmbedTheme = (): EmbedTheme | null => {
  if (!isWebEmbed) {
    return null
  }
  try {
    const params = new URLSearchParams(window.location.search)
    const value = params.get("theme")
    if (value && (ALLOWED_EMBED_THEMES as ReadonlyArray<string>).includes(value)) {
      return value as EmbedTheme
    }
    return "light"
  } catch {
    return "light"
  }
}

// Pinned at module load so ThemeProvider can initialize synchronously with the
// host page's theme and never flash the wrong palette.
const embedTheme: EmbedTheme | null = readEmbedTheme()

const USE_SENTRY = extra.env !== 'local'

if (USE_SENTRY) {
  Sentry.init({
    dsn: 'https://a2c07ef08d1fe763784e782ee1806f1e@o4508212757463040.ingest.us.sentry.io/4510046603378688',
    environment: extra.env,
    release: version,
    replaysOnErrorSampleRate: 1.0,
  })
  
  Sentry.setTag('app', 'client')
}
if (extra.env !== 'local') {
  Analytics.init({
    trackAutomaticEvents: false,
    mixpanelId: '5bcf65a07dd8c76e07e04b1c6aa58c09',
    version: version,
    env: extra.env,
    session_id: pseudoUuid(),
    platform: Platform.OS,
  })
} else {
  console.log('Mixpanel disabled for local development')
}

const queryClient = new QueryClient()

const App = ():React.ReactElement => {
  const [fontsLoaded, setFontsLoaded] = useState(false)
  const [updatesChecked, setUpdatesChecked] = useState(false)
  useEffect(() => {
    const loadFonts = async () => {
      await Font.loadAsync({
        'poppins-regular': require('./src/assets/fonts/Poppins-Regular.ttf'),
        'poppins-bold': require('./src/assets/fonts/Poppins-Bold.ttf'),
        'poppins-semi-bold': require('./src/assets/fonts/Poppins-SemiBold.ttf'),
        ...AntDesign.font,
        ...Entypo.font,
        ...EvilIcons.font,
        ...Feather.font,
        ...FontAwesome.font,
        ...Fontisto.font,
        ...Foundation.font,
        ...Ionicons.font,
        ...MaterialCommunityIcons.font,
        ...MaterialIcons.font,
        ...Octicons.font,
        ...SimpleLineIcons.font,
        ...Zocial.font
      }).catch((e) => {
        log.error(e, { tags: { message: 'error loading fonts' } })
      }).finally(() => {
        setFontsLoaded(true)
      })
    }

    loadFonts()
  }, [])

  useEffect(() => {
    const runUpdateCheck = async () => {
      try {
        await checkForUpdates()
      } catch (e) {
        log.error(e, { tags: { message: 'error checking for updates' } })
      } finally {
        setUpdatesChecked(true)
      }
    }

    runUpdateCheck()
  }, [])

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <ThemeProvider overrideTheme={embedTheme}>
        {isWebEmbed ? (
          <CommunityImpactEmbedding />
        ) : (
          <Provider store={store}>
            <Auth0Provider>
              <AuthProviderWrapper>
                <GestureHandlerRootView style={{ flex: 1 }}>
                  <SafeAreaProvider style={{ flex: 1 }}>
                    <PopoverProvider>
                      <BottomSheetModalProvider>
                        <RouterContext.Provider value={RouterType.ReactRouter}>
                          <QueryClientProvider client={queryClient}>
                            <ContextApp loading={!(fontsLoaded && updatesChecked)}/>
                          </QueryClientProvider>
                        </RouterContext.Provider>
                      </BottomSheetModalProvider>
                    </PopoverProvider>
                  </SafeAreaProvider>
                </GestureHandlerRootView>
              </AuthProviderWrapper>
            </Auth0Provider>
          </Provider>
        )}
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}

type ContextAppProps = {
  loading: boolean
}

const ContextApp = ({loading}: ContextAppProps) => {
  const dispatch = useAppDispatch()
  
  // Initialize app closure detection
  useAppClosure()
  
  useEffect(() => {
    // load device id for use throughout app
    dispatch(loadOrCreateDeviceId())
  }, [])

  if (loading) {
    return <FullScreenLoading loading/>
  }
  
  return (
    <PermissionsProvider>
      <TrackingProvider>
        <StatusBar style={"light"}/>
        <NavigationContainerWrapper/>
      </TrackingProvider>
    </PermissionsProvider>
  )
}

export default USE_SENTRY ? Sentry.wrap(App) : App